import { FLAGS } from '@gozoki/feature-flags'
import { FORECAST_REFERENCE_QUERY_OPTIONS } from '~/lib/forecast-reference-queries'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Download } from 'lucide-react'
import { useState } from 'react'
import { AppContainer } from '~/components/new/app-ui/app-container'
import { AppHeader } from '~/components/new/app-ui/app-header'
import { AppTabs, type AppTab } from '~/components/new/app-ui/app-tabs'
import { Title } from '~/components/new/app-ui/title'
import { Button, buttonVariants } from '~/components/ui/button'
import { TabsContent } from '~/components/ui/tabs'
import { useFeatureFlag } from '~/hooks/useFeatureFlag'
import { api } from '~/lib/tuyau'
import { withRoutePermissions } from '~/utils/rbac'
import { PrevisionsStoreSelector } from '../previsions-long-terme/-previsions-store-selector'
import { CorrectionsToggle } from './-corrections-toggle'
import { VmjDaysSelect } from './-vmj-days-select'
import { WeekdaysToggle } from './-weekdays-toggle'
import { ProductDayCoefsToggle } from './-product-day-coefs-toggle'
import { SeasonModeSelect } from './-season-mode-select'
import { DailyChartModal, type DailyScope } from './-daily-chart-modal'
import { DateRangePicker } from './-date-range-picker'
import { ProductCategorySelector } from './-product-category-selector'
import { CohortView } from './-cohort-view'
import { ProjectForecastView } from './-project-forecast'
import { SeasonTab } from './-season-tab'
import {
  defaultBacktestRange,
  defaultForecastRange,
  presetBacktestRange,
  presetForecastRange,
  shortTermSearchSchema,
  dayCoefModeOf,
} from './-search-schema'
import { ShortTermBacktest } from './-short-term-backtest'
import {
  buildBacktestTree,
  buildForecastTree,
  formatFrDate,
  type ProductNode,
} from './-short-term-data'
import { ShortTermExplainModal } from './-short-term-explain-modal'
import { ShortTermTable } from './-short-term-table'

const TABS: AppTab<'forecast' | 'backtest' | 'season'>[] = [
  { value: 'forecast', label: 'Prévision' },
  { value: 'backtest', label: 'Backtest' },
  { value: 'season', label: 'Saison' },
]

export const Route = createFileRoute('/_authenticated/previsions-produits/')({
  validateSearch: shortTermSearchSchema,
  staticData: { permission: ['read', 'previsions.previsions.forecast'] },
  component: ShortTermPrevisionPage,
})

withRoutePermissions(Route)

function ShortTermPrevisionPage() {
  const enabled = useFeatureFlag(FLAGS.shortTermPrevision)
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const [explained, setExplained] = useState<{
    product: Pick<ProductNode, 'productId' | 'label'>
    storeId: number
    backtest: boolean
  } | null>(null)
  const [dailyScope, setDailyScope] = useState<DailyScope | null>(null)

  const setSearch = (next: Partial<typeof search>) =>
    navigate({ search: (prev) => ({ ...prev, ...next }) })

  const forecastRange = {
    start: search.startDate ?? defaultForecastRange().start,
    end: search.endDate ?? defaultForecastRange().end,
  }
  const backtestRange = {
    start: search.btStartDate ?? defaultBacktestRange().start,
    end: search.btEndDate ?? defaultBacktestRange().end,
  }
  const forecastRangeDays =
    Math.round(
      (new Date(forecastRange.end).getTime() - new Date(forecastRange.start).getTime()) /
        86_400_000,
    ) + 1

  const { data: stores = [] } = useQuery(
    api.forecast.forecastStores.queryOptions({}, FORECAST_REFERENCE_QUERY_OPTIONS),
  )
  const { data: categories = [] } = useQuery(
    api.forecast.productCategories.queryOptions(
      {},
      { enabled, ...FORECAST_REFERENCE_QUERY_OPTIONS },
    ),
  )

  const commonQuery = {
    storeIds: search.storeIds,
    corrections: search.corrections,
    vmjDays: search.vmjDays,
    weekdaysOnly: search.weekdaysOnly,
    dayCoefMode: dayCoefModeOf(search.productDayCoefs),
    seasonMode: search.seasonMode,
    ...(search.productIds.length > 0 ? { productIds: search.productIds } : {}),
  }

  const forecastQuery = useQuery(
    api.forecast.shortTermForecast.queryOptions(
      { query: { ...commonQuery, startDate: forecastRange.start, endDate: forecastRange.end } },
      { enabled: enabled && search.storeIds.length > 0 && search.tab === 'forecast' },
    ),
  )

  const backtestQuery = useQuery(
    api.forecast.shortTermBacktest.queryOptions(
      {
        query: {
          ...commonQuery,
          startDate: backtestRange.start,
          endDate: backtestRange.end,
        },
      },
      { enabled: enabled && search.storeIds.length > 0 && search.tab === 'backtest' },
    ),
  )

  const storeLabelOf = (id: number) =>
    stores.find((store) => store.storeId === id)?.label ?? `Boutique ${id}`
  const categoryNameOf = (id: number) =>
    categories.find((category) => category.id === id)?.name ?? 'Sans catégorie'

  const tree = buildForecastTree(forecastQuery.data?.rows ?? [], storeLabelOf, categoryNameOf)
  const backtestTree = buildBacktestTree(
    backtestQuery.data?.rows ?? [],
    storeLabelOf,
    categoryNameOf,
  )

  if (!enabled) {
    return (
      <>
        <AppHeader>
          <Title>Prévisions court terme UVC</Title>
        </AppHeader>
        <AppContainer>
          <p className="text-muted-foreground">Fonctionnalité non activée.</p>
        </AppContainer>
      </>
    )
  }

  return (
    <>
      <AppHeader>
        <div className="flex items-center gap-3">
          <Title>Prévisions court terme UVC</Title>
          <a
            href="/guide-previsions-produits.pdf"
            download
            className={buttonVariants({ variant: 'outline', size: 'sm' })}
          >
            <Download className="size-4" />
            Guide méthodologique
          </a>
        </div>
      </AppHeader>
      <AppContainer>
        <div className="mb-3 inline-flex gap-1 rounded-lg border p-1">
          <Button
            variant={search.mode === 'stores' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setSearch({ mode: 'stores' })}
          >
            Boutiques
          </Button>
          <Button
            variant={search.mode === 'projects' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setSearch({ mode: 'projects' })}
          >
            Projets
          </Button>
          <Button
            variant={search.mode === 'cohorte' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setSearch({ mode: 'cohorte' })}
          >
            Cohorte (test)
          </Button>
        </div>

        {search.mode === 'cohorte' && (
          <CohortView enabled={enabled} search={search} setSearch={setSearch} />
        )}

        {search.mode === 'stores' && (
          <div className="flex flex-col gap-4">
            {search.tab !== 'season' && (
              <div className="flex flex-wrap items-center gap-3">
                <PrevisionsStoreSelector
                  selectedIds={search.storeIds}
                  onApply={(storeIds) => setSearch({ storeIds })}
                  placeholder="Sélectionner des boutiques"
                  title="Boutiques à prévoir"
                />

                <ProductCategorySelector
                  selectedProductIds={search.productIds}
                  onApply={(productIds) => setSearch({ productIds })}
                />

                <CorrectionsToggle
                  checked={search.corrections}
                  onChange={(corrections) => setSearch({ corrections })}
                />

                <VmjDaysSelect
                  value={search.vmjDays}
                  onChange={(vmjDays) => setSearch({ vmjDays })}
                />

                <WeekdaysToggle
                  checked={search.weekdaysOnly}
                  onChange={(weekdaysOnly) => setSearch({ weekdaysOnly })}
                />

                <ProductDayCoefsToggle
                  checked={search.productDayCoefs}
                  onChange={(productDayCoefs) => setSearch({ productDayCoefs })}
                />

                <SeasonModeSelect
                  value={search.seasonMode}
                  onChange={(seasonMode) => setSearch({ seasonMode })}
                />

                {search.tab === 'forecast' ? (
                  <DateRangePicker
                    start={forecastRange.start}
                    end={forecastRange.end}
                    min={presetForecastRange(1).start}
                    max={presetForecastRange(7).end}
                    onChange={({ start, end }) => setSearch({ startDate: start, endDate: end })}
                    onPreset={(days) => {
                      const range = presetForecastRange(days)
                      setSearch({ startDate: range.start, endDate: range.end })
                    }}
                  />
                ) : (
                  <DateRangePicker
                    start={backtestRange.start}
                    end={backtestRange.end}
                    max={presetBacktestRange(1).end}
                    onChange={({ start, end }) => setSearch({ btStartDate: start, btEndDate: end })}
                    onPreset={(days) => {
                      const range = presetBacktestRange(days)
                      setSearch({ btStartDate: range.start, btEndDate: range.end })
                    }}
                  />
                )}
              </div>
            )}

            <AppTabs tabs={TABS} value={search.tab} onTabChange={(tab) => setSearch({ tab })}>
              <TabsContent value="forecast">
                {search.storeIds.length === 0 && (
                  <p className="text-muted-foreground">Sélectionnez une ou plusieurs boutiques.</p>
                )}
                {search.storeIds.length > 0 && (
                  <>
                    {forecastQuery.data && (
                      <p className="mb-4 text-sm text-muted-foreground">
                        Prévision des ventes du{' '}
                        <strong className="text-foreground">
                          {formatFrDate(forecastQuery.data.startDate)}
                        </strong>{' '}
                        au{' '}
                        <strong className="text-foreground">
                          {formatFrDate(forecastQuery.data.endDate)}
                        </strong>{' '}
                        ({forecastQuery.data.horizonDays} jour
                        {forecastQuery.data.horizonDays > 1 ? 's' : ''}).
                      </p>
                    )}
                    {(() => {
                      if (forecastQuery.isError) {
                        return (
                          <p className="text-amber-600">
                            Erreur lors du calcul de la prévision. Réessayez.
                          </p>
                        )
                      }
                      if (forecastQuery.isPending) {
                        return <p className="text-muted-foreground">Calcul de la prévision…</p>
                      }
                      if (tree.length === 0) {
                        return (
                          <p className="text-muted-foreground">
                            Aucune vente sur la période pour ce périmètre.
                          </p>
                        )
                      }
                      return (
                        <ShortTermTable
                          stores={tree}
                          horizonDays={forecastQuery.data?.horizonDays ?? forecastRangeDays}
                          onExplain={(product, storeId) =>
                            setExplained({ product, storeId, backtest: false })
                          }
                          onShowDaily={setDailyScope}
                        />
                      )
                    })()}
                  </>
                )}
              </TabsContent>

              <TabsContent value="backtest">
                {search.storeIds.length === 0 && (
                  <p className="text-muted-foreground">Sélectionnez une ou plusieurs boutiques.</p>
                )}
                {search.storeIds.length > 0 &&
                  (() => {
                    if (backtestQuery.isError) {
                      return (
                        <p className="text-amber-600">
                          Erreur lors du calcul du backtest. Réessayez.
                        </p>
                      )
                    }
                    if (backtestQuery.isPending || !backtestQuery.data) {
                      return <p className="text-muted-foreground">Backtest en cours…</p>
                    }
                    if (backtestTree.length === 0) {
                      return (
                        <p className="text-muted-foreground">
                          {backtestQuery.data.excludedStoreCount > 0
                            ? `Aucune boutique n'a assez d'historique pour un backtest à cette date d'origine (${backtestQuery.data.excludedStoreCount} retirée(s)).`
                            : 'Pas assez de données pour un backtest sur ce périmètre.'}
                        </p>
                      )
                    }
                    return (
                      <ShortTermBacktest
                        stores={backtestTree}
                        metrics={backtestQuery.data.metrics}
                        startDate={backtestQuery.data.startDate}
                        endDate={backtestQuery.data.endDate}
                        excludedStoreCount={backtestQuery.data.excludedStoreCount}
                        formatDate={formatFrDate}
                        onShowDaily={setDailyScope}
                        onExplain={(product, storeId) =>
                          setExplained({ product, storeId, backtest: true })
                        }
                        correctionsOn={search.corrections}
                      />
                    )
                  })()}
              </TabsContent>

              <TabsContent value="season">
                <SeasonTab />
              </TabsContent>
            </AppTabs>
          </div>
        )}

        {search.mode === 'projects' && (
          <ProjectForecastView
            enabled={enabled}
            search={search}
            setSearch={setSearch}
            categoryNameOf={categoryNameOf}
          />
        )}

        {explained && (
          <ShortTermExplainModal
            storeId={explained.storeId}
            product={explained.product}
            storeLabel={storeLabelOf(explained.storeId)}
            startDate={explained.backtest ? backtestRange.start : forecastRange.start}
            endDate={explained.backtest ? backtestRange.end : forecastRange.end}
            corrections={search.corrections}
            vmjDays={search.vmjDays}
            weekdaysOnly={search.weekdaysOnly}
            productDayCoefs={search.productDayCoefs}
            seasonMode={search.seasonMode}
            backtest={explained.backtest}
            onClose={() => setExplained(null)}
          />
        )}

        {dailyScope && (
          <DailyChartModal
            scope={dailyScope}
            mode={search.tab === 'backtest' ? 'backtest' : 'forecast'}
            range={search.tab === 'backtest' ? backtestRange : forecastRange}
            filterProductIds={search.productIds}
            corrections={search.corrections}
            vmjDays={search.vmjDays}
            weekdaysOnly={search.weekdaysOnly}
            productDayCoefs={search.productDayCoefs}
            seasonMode={search.seasonMode}
            onClose={() => setDailyScope(null)}
          />
        )}
      </AppContainer>
    </>
  )
}
